<div class="widget">
  @let isWsOpen = isWsOpen$ | async;
  @let threadStatus = threadStatus$ | async;
  @let isCurrentUserThreadParticipant = isCurrentUserThreadParticipant$ | async;
  @let isMine = isMine$ | async;
  @let threadRawStatus = threadRawStatus$ | async;
  @let canCloseThread = canCloseThread$ | async;
  @let isLastMessageByCurrentUser = isLastMessageByCurrentUser$ | async;
  @if (!isWsOpen) {
    <div class="ws-error">
      <alg-error i18n-message message="Connection with the forum lost. Check your connection, or reload the app if it does not recover automatically."
        icon="ph-duotone ph-wifi-slash"
       />
    </div>
  } @else {
    @let currentState = state();
    @if (currentState.isError) {
      <div class="widget-error">
        <alg-error i18n-message message="Unable to load the thread messages."
          icon="ph-duotone ph-warning-circle"
         />
      </div>
    } @else if (currentState.isFetching && !currentState.data) {
      <div class="widget-loading">
        <alg-loading />
        <div class="loading-text" i18n>Loading...</div>
      </div>
    } @else {
      <div class="widget-body">
          @let followStatus = followStatus$ | async;
          @let threadIdForIndicator = threadId$ | async;
          @let canCurrentUserLoadAnswers = canCurrentUserLoadAnswers$ | async;
          @if (!isMine && threadIdForIndicator && threadStatus?.data?.open) {
            @if (participantUser$ | async; as participantUser) {
              @if (participantUser.name) {
                <alg-thread-top-indicator [followStatus]="followStatus!"
                  [isThreadOpen]="!!threadStatus?.data?.open"
                  [layout]="indicatorLayout()"
                  (followChanged)="onFollowChanged(threadIdForIndicator, $event)"
                 />
              }
            }
          }
          @let showTopIndicator = !isMine && threadIdForIndicator && threadStatus?.data?.open && (participantUser$ | async)?.name;
          <div class="widget-scroll" [class.with-top-indicator]="showTopIndicator" #messagesScroll>
            @if (isCurrentUserThreadParticipant && threadStatus?.isReady && currentState.data?.length === 0) {
              <div class="no-messages" i18n>
              In this panel, you can request help from other users about the content you are on.
              People who have already validated this activity will see your request and be able to answer.
            </div>
            } @else {
              @if (threadIdForIndicator) {
                @for (event of currentState.data; track event.time) {
                  <alg-thread-message class="thread-message"
                    [threadId]="threadIdForIndicator"
                    [event]="event"
                    [canCurrentUserLoadAnswers]="canCurrentUserLoadAnswers ?? false"
                    [itemRoute]="{ id: threadIdForIndicator.itemId, type: 'Task' } | itemRoute"
                   />
                }
                @if (currentState.isFetching && currentState.data) {
                  @if (isLoadingActivities() && !hasActivitiesData() && hasMessagesData()) {
                    <div class="refetching-indicator" i18n>Loading the activities...</div>
                  } @else if (isLoadingMessages() && !hasMessagesData() && hasActivitiesData()) {
                    <div class="refetching-indicator" i18n>Loading the messages...</div>
                  } @else if (isLoadingActivities() || isLoadingMessages()) {
                    <div class="refetching-indicator" i18n>Looking for new events...</div>
                  }
                }
              }
            }
            @if (threadIdForIndicator; as threadId) {
              @let isThreadOpened = !!(threadStatus && !!threadStatus.data && !!threadStatus.data.open);
              <div class="footer-panel">
                @if (isLastMessageByCurrentUser) {
                  @if (isMine && threadRawStatus === 'waiting_for_participant') {
                    <p class="assignment-suggestion">
                      <ng-container i18n>The thread is assigned to you. When you are done with your messages,</ng-container>
                      {{ ' ' }}<button type="button" class="assignment-suggestion-action" (click)="changeAssignment(1, threadId)" i18n>assign it to helpers</button>{{ ' ' }}
                      <ng-container i18n>so that they can better notice they have to answer.</ng-container>
                      @if (canCloseThread) {
                        {{ ' ' }}<ng-container i18n>If you think the discussion has been concluded, you can also</ng-container>
                        {{ ' ' }}<button type="button" class="assignment-suggestion-action" (click)="changeThreadStatus({ open: false, threadId: threadId, isParticipant: true })" i18n>close it</button><ng-container i18n>.</ng-container>
                      }
                    </p>
                  }
                  @if (!isMine && threadRawStatus === 'waiting_for_trainer') {
                    <p class="assignment-suggestion">
                      <ng-container i18n>The thread is assigned to helpers. When you are done with your messages,</ng-container>
                      {{ ' ' }}
                      @if (participantUser$ | async; as participantUser) {
                        <button type="button" class="assignment-suggestion-action" (click)="changeAssignment(0, threadId)"><ng-container i18n>assign it to</ng-container>{{ ' ' }}{{ participantUser.name }}</button>
                      }
                      {{ ' ' }}<ng-container i18n>so that they can better notice they have to answer.</ng-container>
                      @if (canCloseThread) {
                        {{ ' ' }}<ng-container i18n>If you think the discussion has been concluded, you can also</ng-container>
                        {{ ' ' }}<button type="button" class="assignment-suggestion-action" (click)="changeThreadStatus({ open: false, threadId: threadId, isParticipant: false })" i18n>close it</button><ng-container i18n>.</ng-container>
                      }
                    </p>
                  }
                }
                <form class="send-form" [formGroup]="form" (ngSubmit)="sendMessage(threadId, isThreadOpened, !!isMine)">
                  <textarea
                    class="textarea"
                    placeholder="Send a message..."
                    formControlName="messageToSend"
                    [rows]="1"
                    algAutoResize
                    (keydown.enter)="$event.preventDefault(); sendMessage(threadId, isThreadOpened, !!isMine)"
                    #messageToSendEl
                  ></textarea>
                  <button
                    class="size-l with-bg send-button"
                    type="submit"
                    icon="ph ph-paper-plane-tilt"
                    alg-button-icon
                    [disabled]="!form.get('messageToSend')?.value?.trim() || !!(disableControls$ | async)"
                  ></button>
                </form>
                <div class="thread-status">
                  @if (threadStatus?.data?.open) {
                    <div class="thread-assignment">
                      <span class="thread-assignment-label" i18n>Assigned to:</span>
                      @if (assignmentItems$ | async; as assignmentItems) {
                        <alg-selection class="small"
                          [class.on-colored-bg]="indicatorLayout() === 'default'"
                          [items]="assignmentItems"
                          [selected]="(threadAssignmentIndex$ | async) ?? 1"
                          (change)="changeAssignment($event, threadId)"
                         />
                      }
                    </div>
                  } @else {
                    <div class="thread-status-caption">
                      @if (threadStatus?.data?.open === false) {
                        <span i18n>This thread is closed</span>
                      }
                      @if (threadStatus?.isFetching) {
                        <span i18n>Loading thread status...</span>
                      }
                      @if (threadStatus?.isError) {
                        <span i18n>Error while loading thread info</span>
                      }
                      @if (threadStatus === undefined) {
                        <span i18n>Could not load thread</span>
                      }
                    </div>
                  }
                  @let canSwitch = !!threadStatus && !!threadStatus.data
                    && ((threadStatus.data.open && threadStatus.data.canClose)
                      || (!threadStatus.data.open && threadStatus.data.canOpen));
                  <div
                    [algTooltip]="threadStatusTooltip"
                    [tooltipDisabled]="canSwitch"
                    tooltipPosition="top"
                  >
                    @if (threadStatus && threadStatus.data) {
                      <button
                        type="button"
                        [disabled]="!canSwitch"
                        alg-button
                        (click)="changeThreadStatus({ open: !threadStatus.data.open, threadId: threadId, isParticipant: !!isMine })"
                      >
                        @if (threadStatus.data.open) {
                          <span i18n>Close thread</span>
                        } @else {
                          <span i18n>Open this thread</span>
                        }
                      </button>
                    }
                  </div>
                  <ng-template #threadStatusTooltip>
                    @if (threadStatus?.data?.open) {
                      <span i18n>
                      Only the user of the thread can close it.
                    </span>
                    } @else {
                      <span i18n>You are not allowed to open this thread.</span>
                    }
                  </ng-template>
                </div>
              </div>
            }
          </div>
        </div>
      }
  }
</div>

